import React, { useState, useCallback, useEffect, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';

const ToastContext = createContext(null);

let toastId = 0;

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};

const icons = {
  success: CheckCircle,
  error: AlertCircle,
  info: Info,
};

/* Success uses the success green, not the accent. The accent is violet in this
   theme, and a violet "Saved" toast reads as branding rather than as
   confirmation. Info stays neutral on purpose: a third semantic colour would
   dilute the accent, and "here is some information" is not worth a hue. */
const colors = {
  success: 'text-success',
  error: 'text-red-300',
  info: 'text-zinc-300',
};

const accents = {
  success: 'bg-success',
  error: 'bg-destructive',
  info: 'bg-zinc-500',
};

function ToastItem({ id, message, type = 'info', onDismiss }) {
  const Icon = icons[type] || icons.info;
  const color = colors[type] || colors.info;
  const accent = accents[type] || accents.info;

  useEffect(() => {
    const timer = setTimeout(() => onDismiss(id), 4000);
    return () => clearTimeout(timer);
  }, [id, onDismiss]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      className="ui-popover pointer-events-auto relative flex items-center gap-3 pl-4 pr-2 py-3 max-w-sm w-full overflow-hidden"
      role={type === 'error' ? 'alert' : 'status'}
      aria-live={type === 'error' ? 'assertive' : 'polite'}
    >
      <span className={`absolute left-0 top-0 bottom-0 w-1 ${accent}`} />
      <Icon className={`w-5 h-5 shrink-0 ${color}`} />
      <p className="text-sm text-zinc-100 flex-1 leading-snug whitespace-pre-line">{message}</p>
      <button
        type="button"
        onClick={() => onDismiss(id)}
        className="ui-icon-btn ui-icon-btn-sm shrink-0"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </motion.div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((message, type = 'info') => {
    const id = ++toastId;
    setToasts((prev) => [...prev.slice(-4), { id, message, type }]);
    return id;
  }, []);

  const value = React.useMemo(() => ({
    toast: (message) => toast(message, 'info'),
    success: (message) => toast(message, 'success'),
    error: (message) => toast(message, 'error'),
  }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="fixed bottom-4 right-4 left-4 md:left-auto md:w-96 z-[9999] flex flex-col gap-2 items-end pointer-events-none pb-safe pr-safe">
          <AnimatePresence mode="popLayout">
            {toasts.map((t) => (
              <ToastItem key={t.id} {...t} onDismiss={dismiss} />
            ))}
          </AnimatePresence>
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}
